// SOP 补充：每次运行（跑完 / 没跑完都算）整理出的建议，业主逐条「采纳 / 不要」，可先改文字；
// 点「写进 SOP」把采纳的一次写入并出新版本，下次运行就按新 SOP 跑。下面是每次更新了什么的记录。
import { useState } from "react";
import type { SopItemDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { useLoad } from "../lib/hooks";
import { mediaUrl } from "../lib/net";
import { useMediaToken } from "../lib/media";
import { useDialog } from "./Dialogs";
import { useLightbox } from "./Lightbox";

const KIND: Record<SopItemDTO["kind"], string> = { step: "步骤", rule: "经验", field: "字段", file: "文件" };

export function SopSupplements({ typeId, taskId }: { typeId: string; taskId?: string }) {
  const q = useLoad(() => api.sopItems(typeId), [typeId]);
  const dialog = useDialog();
  const lb = useLightbox();
  const token = useMediaToken();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [newText, setNewText] = useState("");
  const [newKind, setNewKind] = useState<"rule" | "step">("rule");
  const [showRejected, setShowRejected] = useState(false);
  const items = q.data ?? [];
  const pending = items.filter((i) => i.status === "pending");
  const accepted = items.filter((i) => i.status === "accepted");
  const rejected = items.filter((i) => i.status === "rejected");
  const applied = items.filter((i) => i.status === "applied");

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id); setError(null);
    try { await fn(); await q.reload(); } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const decide = (it: SopItemDTO, status: "pending" | "accepted" | "rejected") =>
    run(it.id, () => api.decideSopItem(it.id, { status, ...(edits[it.id] !== undefined ? { detail: edits[it.id] } : {}) }));
  const apply = async () => {
    const ok = await dialog.confirm({ title: `把采纳的 ${accepted.length} 条写进 SOP？`, body: <p>写入后出一个新的 SOP 版本，下次运行（包括「从头重启」）就按新版本跑；字段 / 文件同时加进资料清单。以后可以在类型的「版本历史」里回滚。</p>, okText: "写进 SOP" });
    if (!ok) return;
    await run("apply", async () => { const r = await api.applySopItems(typeId); await dialog.confirm({ title: `已写进 SOP v${r.version}`, body: <p>共 {r.count} 条。现在可以重新开跑。</p>, okText: "知道了" }); });
  };

  const card = (it: SopItemDTO) => {
    const mine = taskId && it.taskId === taskId;
    const shot = it.screenshot ? mediaUrl(it.screenshot, token) : null;
    const editable = it.status === "pending" || it.status === "accepted";
    return (
      <div key={it.id} className={`sop-item sop-${it.status}`} data-testid="sop-item">
        <div className="row" style={{ gap: 6, alignItems: "center" }}>
          <span className={`sop-kind sop-kind-${it.kind}`}>{KIND[it.kind]}</span>
          <b className="sop-title">{it.title}</b>
          {mine && <span className="small muted">本任务</span>}
          <span className="spacer" />
          <span className="small muted" title={fmtDateTime(it.createdAt)}>{it.source} · {fmtDateTime(it.createdAt)}</span>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "flex-start", marginTop: 6 }}>
          {editable ? (
            <textarea className="sop-detail" value={edits[it.id] ?? it.detail} rows={Math.min(6, Math.max(2, Math.ceil((edits[it.id] ?? it.detail).length / 60)))} onChange={(e) => setEdits((s) => ({ ...s, [it.id]: e.target.value }))} aria-label="写进 SOP 的内容" />
          ) : <p className="sop-detail-text">{it.detail}</p>}
          {shot && <img className="sop-shot" src={shot} alt="依据截图" onClick={() => lb.open(shot)} />}
        </div>
        {it.evidence && <p className="small muted" style={{ margin: "4px 0 0" }}>依据：{it.evidence}</p>}
        {it.status === "pending" && (
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" disabled={!!busy} onClick={() => void decide(it, "accepted")}>✓ 采纳</button>
            <button className="btn" disabled={!!busy} onClick={() => void decide(it, "rejected")}>✕ 不要</button>
          </div>
        )}
        {it.status === "accepted" && (
          <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
            <span className="small ok-text">已采纳（{it.decidedBy}），等写进 SOP</span>
            {edits[it.id] !== undefined && edits[it.id] !== it.detail && <button className="btn" disabled={!!busy} onClick={() => void decide(it, "accepted")}>保存修改</button>}
            <button className="btn" disabled={!!busy} onClick={() => void decide(it, "pending")}>撤回</button>
          </div>
        )}
        {it.status === "rejected" && (
          <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
            <span className="small muted">不要（{it.decidedBy}）</span>
            <button className="btn" disabled={!!busy} onClick={() => void decide(it, "pending")}>改回待定</button>
          </div>
        )}
      </div>
    );
  };

  // 更新记录：按写入的版本分组，新的在前
  const groups = new Map<string, SopItemDTO[]>();
  for (const it of [...applied].sort((a, b) => (b.appliedAt ?? "").localeCompare(a.appliedAt ?? ""))) {
    const k = it.appliedVersion ?? "?";
    groups.set(k, [...(groups.get(k) ?? []), it]);
  }

  return (
    <div className="sop-supp" data-testid="sop-supplements">
      {lb.node}
      <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>SOP 补充 · 待你决定 {pending.length} 条</h3>
        <span className="spacer" />
        <button className="btn btn-primary" disabled={!accepted.length || !!busy} onClick={() => void apply()} data-testid="sop-apply">
          {busy === "apply" ? "写入中…" : `把采纳的 ${accepted.length} 条写进 SOP`}
        </button>
      </div>
      <p className="small muted">每次运行结束（跑完或没跑完）系统都会把这次学到的整理成一条条建议。你逐条点「采纳」或「不要」，可以先改文字；采纳完点右上角写进 SOP，再开跑就按新 SOP 走。</p>
      {error && <p className="notice err">{error}</p>}
      {q.loading && !q.data && <p className="muted">加载中…</p>}
      {pending.length > 0 ? pending.map(card) : q.data && <p className="small ok-text">✓ 没有待决定的建议</p>}
      {accepted.length > 0 && <><h4>已采纳，等写进 SOP（{accepted.length}）</h4>{accepted.map(card)}</>}

      <div className="sop-add">
        <h4>自己加一条</h4>
        <div className="row" style={{ gap: 8, alignItems: "flex-start" }}>
          <select value={newKind} onChange={(e) => setNewKind(e.target.value as "rule" | "step")} aria-label="类别">
            <option value="rule">经验</option>
            <option value="step">步骤</option>
          </select>
          <textarea className="sop-detail" rows={2} placeholder="例：草稿当天 23:59 过期，同一份申请必须当天跑完" value={newText} onChange={(e) => setNewText(e.target.value)} />
          <button className="btn" disabled={!newText.trim() || !!busy} onClick={() => void run("add", async () => { await api.addSopItem(typeId, { kind: newKind, detail: newText }); setNewText(""); })}>加入（直接算采纳）</button>
        </div>
      </div>

      {rejected.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-link" onClick={() => setShowRejected((v) => !v)}>{showRejected ? "收起" : "看看"}不要的 {rejected.length} 条</button>
          {showRejected && rejected.map(card)}
        </div>
      )}

      <h3 style={{ marginTop: 20 }}>SOP 更新记录</h3>
      {groups.size === 0 ? <p className="small muted">还没有写进 SOP 的补充</p> : [...groups].map(([ver, list]) => (
        <div key={ver} className="sop-log">
          <div className="row" style={{ gap: 8 }}>
            <b>{ver === "运行中" ? "运行中直接生效（补字段时加进字段表）" : `SOP v${ver}`}</b>
            <span className="small muted">{list[0]!.appliedAt ? fmtDateTime(list[0]!.appliedAt) : ""}{ver !== "运行中" && list[0]!.decidedBy ? ` · ${list[0]!.decidedBy}` : ""} · {list.length} 条</span>
          </div>
          <ul>{list.map((it) => <li key={it.id}><span className={`sop-kind sop-kind-${it.kind}`}>{KIND[it.kind]}</span> {it.detail}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}
